import React, { useState, useMemo } from 'react';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import { CalendarIcon, Check } from 'lucide-react';
import { cn } from '@/lib/core/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { UnifiedEmployeeSelect } from '@/components/ui/unified-employee-select';
import { TimeRangeSelector } from '@/components/ui/time-range-selector';
import type { AIQuestionInputProps, AIQuestionOption } from './types';

// Letter labels for choice options
const CHOICE_LETTERS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

/**
 * AIQuestionInput - Renders the appropriate input component based on question type
 */
export const AIQuestionInput: React.FC<AIQuestionInputProps> = ({
  question,
  businessId,
  value,
  onChange,
  disabled = false,
}) => {
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Parse constraints with defaults
  const constraints = question.constraints || {};

  // Render based on question type
  switch (question.type) {
    // ========================================================================
    // EMPLOYEE SELECT
    // ========================================================================
    case 'employee_select': {
      const multiSelect = constraints.multiSelect ?? false;

      // Build availability config if provided
      const availabilityConfig = constraints.showAvailability && constraints.availabilityDate
        ? {
            startTime: constraints.availabilityStartTime
              ? new Date(`${constraints.availabilityDate}T${constraints.availabilityStartTime}`)
              : undefined,
            endTime: constraints.availabilityEndTime
              ? new Date(`${constraints.availabilityDate}T${constraints.availabilityEndTime}`)
              : undefined,
          }
        : undefined;

      return (
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-muted-foreground">
            {question.text}
          </Label>
          <UnifiedEmployeeSelect
            businessId={businessId}
            value={value as string | string[] | undefined}
            onValueChange={(val) => onChange(val)}
            variant="command"
            config={{
              mode: multiSelect ? 'multiple' : 'single',
              includeUnassigned: false,
            }}
            showAvailabilityIndicator={constraints.showAvailability}
            availabilityCheckConfig={availabilityConfig}
            showRoleInfo
            disabled={disabled}
            placeholder="Select employee..."
            triggerClassName="w-full"
          />
        </div>
      );
    }

    // ========================================================================
    // DATE PICKER
    // ========================================================================
    case 'date': {
      const selectedDate = value ? new Date(value as string) : undefined;
      const minDate = constraints.minDate ? new Date(constraints.minDate) : undefined;
      const maxDate = constraints.maxDate ? new Date(constraints.maxDate) : undefined;

      return (
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-muted-foreground">
            {question.text}
          </Label>
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  'w-full justify-start text-left font-normal',
                  !selectedDate && 'text-muted-foreground'
                )}
                disabled={disabled}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {selectedDate ? format(selectedDate, 'PPP') : 'Pick a date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => {
                  onChange(date ? format(date, 'yyyy-MM-dd') : null);
                  setCalendarOpen(false);
                }}
                disabled={(date) => {
                  if (minDate && date < minDate) return true;
                  if (maxDate && date > maxDate) return true;
                  return false;
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      );
    }

    // ========================================================================
    // WEEK PICKER
    // ========================================================================
    case 'week': {
      const selectedDate = value ? new Date(value as string) : undefined;
      const weekStart = selectedDate ? startOfWeek(selectedDate, { weekStartsOn: 1 }) : undefined;
      const weekEnd = selectedDate ? endOfWeek(selectedDate, { weekStartsOn: 1 }) : undefined;

      return (
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-muted-foreground">
            {question.text}
          </Label>
          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  'w-full justify-start text-left font-normal',
                  !selectedDate && 'text-muted-foreground'
                )}
                disabled={disabled}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {weekStart && weekEnd
                  ? `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`
                  : 'Pick a week'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(date) => {
                  if (date) {
                    const start = startOfWeek(date, { weekStartsOn: 1 });
                    onChange(format(start, 'yyyy-MM-dd'));
                  } else {
                    onChange(null);
                  }
                  setCalendarOpen(false);
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>
      );
    }

    // ========================================================================
    // MONTH PICKER
    // ========================================================================
    case 'month': {
      const selectedDate = value ? new Date(value as string) : undefined;
      const months = useMemo(() => {
        const result = [];
        const now = new Date();
        for (let i = -3; i <= 12; i++) {
          const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
          result.push({
            value: format(date, 'yyyy-MM'),
            label: format(date, 'MMMM yyyy'),
          });
        }
        return result;
      }, []);

      return (
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-muted-foreground">
            {question.text}
          </Label>
          <Select
            value={value as string || ''}
            onValueChange={(val) => onChange(val || null)}
            disabled={disabled}
          >
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select month">
                {selectedDate ? format(selectedDate, 'MMMM yyyy') : 'Select month'}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {months.map((month) => (
                <SelectItem key={month.value} value={month.value}>
                  {month.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }

    // ========================================================================
    // TIME RANGE
    // ========================================================================
    case 'time_range': {
      const timeValue = value as { startTime: string; endTime: string } | undefined;

      return (
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-muted-foreground">
            {question.text}
          </Label>
          <TimeRangeSelector
            value={timeValue || { startTime: '09:00', endTime: '17:00' }}
            onChange={(val) => onChange(val)}
            minHour={constraints.minHour ?? 0}
            maxHour={constraints.maxHour ?? 24}
            disabled={disabled}
          />
        </div>
      );
    }

    // ========================================================================
    // SINGLE CHOICE
    // ========================================================================
    case 'single_choice': {
      const options = question.options || [];

      return (
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-muted-foreground">
            {question.text}
          </Label>
          <div className="flex flex-wrap gap-2">
            {options.map((option, index) => {
              const isSelected = value === option.value;
              const letter = CHOICE_LETTERS[index] || String(index + 1);

              return (
                <Button
                  key={option.value}
                  variant={isSelected ? 'default' : 'outline'}
                  size="sm"
                  className={cn(
                    'flex items-center gap-1.5',
                    isSelected && 'ring-2 ring-primary/20'
                  )}
                  onClick={() => onChange(option.value)}
                  disabled={disabled}
                >
                  <span className="text-xs font-mono opacity-60">{letter}.</span>
                  <span>{option.label}</span>
                  {isSelected && <Check className="h-3 w-3 ml-1" />}
                </Button>
              );
            })}
          </div>
          {options.some(o => o.description) && value && (
            <p className="text-xs text-muted-foreground mt-1">
              {options.find(o => o.value === value)?.description}
            </p>
          )}
        </div>
      );
    }

    // ========================================================================
    // MULTIPLE CHOICE
    // ========================================================================
    case 'multiple_choice': {
      const options = question.options || [];
      const selectedValues = (value as string[] | undefined) || [];

      return (
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-muted-foreground">
            {question.text}
          </Label>
          <div className="space-y-2">
            {options.map((option, index) => {
              const isSelected = selectedValues.includes(option.value);
              const letter = CHOICE_LETTERS[index] || String(index + 1);

              return (
                <div key={option.value} className="flex items-start gap-2">
                  <Checkbox
                    id={`${question.id}-${option.value}`}
                    checked={isSelected}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        onChange([...selectedValues, option.value]);
                      } else {
                        onChange(selectedValues.filter(v => v !== option.value));
                      }
                    }}
                    disabled={disabled}
                  />
                  <div className="flex-1">
                    <label
                      htmlFor={`${question.id}-${option.value}`}
                      className="text-sm cursor-pointer"
                    >
                      <span className="text-xs font-mono opacity-60 mr-1">{letter}.</span>
                      {option.label}
                    </label>
                    {option.description && (
                      <p className="text-xs text-muted-foreground">{option.description}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    // ========================================================================
    // YES/NO
    // ========================================================================
    case 'yes_no': {
      return (
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-muted-foreground">
            {question.text}
          </Label>
          <div className="flex gap-2">
            <Button
              variant={value === 'yes' ? 'default' : 'outline'}
              size="sm"
              className={cn(
                'flex-1',
                value === 'yes' && 'bg-green-600 hover:bg-green-700'
              )}
              onClick={() => onChange('yes')}
              disabled={disabled}
            >
              Yes
            </Button>
            <Button
              variant={value === 'no' ? 'default' : 'outline'}
              size="sm"
              className={cn(
                'flex-1',
                value === 'no' && 'bg-red-600 hover:bg-red-700'
              )}
              onClick={() => onChange('no')}
              disabled={disabled}
            >
              No
            </Button>
          </div>
        </div>
      );
    }

    // ========================================================================
    // NUMBER
    // ========================================================================
    case 'number': {
      return (
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-muted-foreground">
            {question.text}
          </Label>
          <Input
            type="number"
            value={value as number | '' || ''}
            onChange={(e) => {
              const val = e.target.value;
              onChange(val === '' ? null : Number(val));
            }}
            min={constraints.min}
            max={constraints.max}
            step={constraints.step ?? 1}
            placeholder={constraints.placeholder || 'Enter a number'}
            disabled={disabled}
            className="w-full"
          />
        </div>
      );
    }

    // ========================================================================
    // TEXT
    // ========================================================================
    case 'text': {
      return (
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-muted-foreground">
            {question.text}
          </Label>
          <Input
            type="text"
            value={(value as string) || ''}
            onChange={(e) => onChange(e.target.value || null)}
            maxLength={constraints.maxLength}
            placeholder={constraints.placeholder || 'Enter text'}
            disabled={disabled}
            className="w-full"
          />
        </div>
      );
    }

    // ========================================================================
    // LONG TEXT
    // ========================================================================
    case 'long_text': {
      return (
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-muted-foreground">
            {question.text}
          </Label>
          <Textarea
            value={(value as string) || ''}
            onChange={(e) => onChange(e.target.value || null)}
            maxLength={constraints.maxLength}
            placeholder={constraints.placeholder || 'Enter your response'}
            disabled={disabled}
            className="w-full min-h-[80px] resize-none"
          />
          {constraints.maxLength && (
            <p className="text-xs text-muted-foreground text-right">
              {((value as string) || '').length}/{constraints.maxLength}
            </p>
          )}
        </div>
      );
    }

    // ========================================================================
    // DEFAULT / UNKNOWN
    // ========================================================================
    default:
      return (
        <div className="space-y-1.5">
          <Label className="text-xs font-medium text-muted-foreground">
            {question.text}
          </Label>
          <Input
            type="text"
            value={(value as string) || ''}
            onChange={(e) => onChange(e.target.value || null)}
            placeholder="Enter your answer"
            disabled={disabled}
            className="w-full"
          />
        </div>
      );
  }
};

export default AIQuestionInput;
